/*!
 * GlyphHero — 写真の上に「色タイル＋等幅グリフ」のモザイクを重ね、
 * コマ送りでパラパラ動かすキービジュアル用レンダラー（依存なし）
 *
 * mode:
 *   'subject' … 写真はそのまま。人物など被写体の部分だけがモザイクになって揺らぐ
 *   'patch'   … 写真はそのまま。ランダムな矩形のモザイクがパラパラ出ては消える
 *   'full'    … 画面全体を写真の色のままモザイク化
 *
 * style:
 *   'tonal'   … 写真の色だけで構成。タイルはグラデーション、文字は同系色、差し色は写真から抽出
 *   'classic' … 単色タイル＋白黒の文字＋固定の差し色（参考動画寄り）
 *
 * 使い方:
 *   const hero = GlyphHero.mount(document.querySelector('.kv'), {
 *     src: 'assets/library.webp',
 *     mode: 'subject',
 *   });
 *   hero.set({ mode: 'patch' });  // パラメータ変更
 *   hero.destroy();               // 破棄
 */
(function (global) {
  'use strict';

  const DEFAULTS = {
    src: '',
    crossOrigin: null,   // 画像を別ドメイン(CDN等)に置く場合は 'anonymous'（サーバー側のCORS許可も必要）
    mode: 'subject',
    style: 'tonal',
    cell: 14,            // セルの一辺(CSS px)
    fps: 5,              // コマ数。低いほど「パラパラ感」が強く、ゆっくりになる
    fade: 0.9,           // コマ間のクロスフェード（1コマの何割をかけて溶かすか。0でパッと切替）
    opacity: 0.9,        // モザイク全体の不透明度（下げるほど写真が透ける）
    softEdge: 0.55,      // 被写体の輪郭付近のセルの不透明度（境目をぼかす）
    glyphAlpha: 0.85,    // 文字の不透明度
    threshold: 0.64,     // subject: これより濃い部分をモザイク化 (0-1)
    edge: 0.8,           // subject: 周囲とのコントラストで被写体を拾う強さ
    chroma: 0.5,         // subject: 彩度で被写体を拾う強さ
    density: 0.96,       // subject: 被写体セルのうち毎コマ表示する割合（残りは写真が覗く）
    patchRate: 0.4,      // patch: 1コマあたりの矩形の発生しやすさ
    patchMax: 14,        // patch: 同時に出る矩形の最大数
    flicker: 0.15,       // 1コマごとにグリフが入れ替わる確率
    jitter: 0.2,         // サンプリング位置の揺れ（セル比）
    accent: 0.01,        // 色付きグリフの出現率
    accents: ['#7b61ff', '#2f9e5b', '#e0752d', '#d9468f', '#3b82f6'], // classic の差し色
    gridLine: 'rgba(255,255,255,0.55)', // classic の目地の色
    tonalGrid: 0.16,     // tonal の目地の明るさ (0-1)
    sheen: 0.08,         // tonal のタイルの陰影（上が明るく下が暗い）の強さ
    font: '"M PLUS 1 Code", "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
    focusX: 0.5,         // object-position 相当（cover時の切り抜き基準）
    focusY: 0.5,
    intro: 2600,         // 全面モザイク→写真へ溶けていく導入(ms)。0で無効
    hover: true,         // カーソル周辺をモザイク化
    hoverRadius: 120,
  };

  // 濃度順のグリフ（薄い→濃い）。Maison AI 指定の字種：英字 m a i s o n／カタカナ メ ゾ ン エ ー ア イ／記号 # % @ : / .／数字 8
  const RAMP = ['.', ':', '/', 'ー', 'i', 's', 'o', 'n', 'a', 'イ', 'ン', 'エ', 'ア', 'm', 'ゾ', 'メ', '%', '8', '#', '@'];

  function mount(container, opts) {
    const o = Object.assign({}, DEFAULTS, opts);
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;';
    if (getComputedStyle(container).position === 'static') container.style.position = 'relative';
    container.insertBefore(canvas, container.firstChild);
    const ctx = canvas.getContext('2d');

    // モザイクだけを描くレイヤー（前コマ／現コマ）と、クロスフェード合成用
    const layers = [document.createElement('canvas'), document.createElement('canvas')];
    const lctxs = layers.map((l) => l.getContext('2d'));
    const mix = document.createElement('canvas');
    const mctx = mix.getContext('2d');
    let cur = 0, fadeStart = 0, fadeDur = 0, dirty = true;

    const sample = document.createElement('canvas');
    const sctx = sample.getContext('2d', { willReadFrequently: true });

    const img = new Image();
    img.decoding = 'async';
    if (o.crossOrigin) img.crossOrigin = o.crossOrigin;

    let W = 0, H = 0, dpr = 1, cols = 0, rows = 0;
    let cover = null;        // セル座標での cover 配置（サンプリング用）
    let coverPx = null;      // CSS px での cover 配置（写真描画用）
    let lo = 0, hi = 1;      // 輝度の正規化レンジ
    let contrast = null;     // セルごとの局所コントラスト (0-1)
    let chroma = null;       // セルごとの彩度 (0-1)
    let seeds = null;        // セルごとの乱数シード
    let accentMap = null;    // セルごとの色付きグリフ状態
    let patchMask = null;    // patch モードのマスク
    let patches = [];
    let palette = [];        // 写真から抽出した差し色 [[r,g,b], ...]
    let sheenPattern = null; // tonal 用：1セル分の陰影グラデーション
    let raf = 0, last = 0, start = 0;
    let visible = true, ready = false, destroyed = false;
    const mouse = { x: -9999, y: -9999, active: false };

    function rand(n) { // 安定した疑似乱数 0-1
      n = (n ^ 61) ^ (n >>> 16); n += n << 3; n ^= n >>> 4;
      n = Math.imul(n, 0x27d4eb2d); n ^= n >>> 15;
      return (n >>> 0) / 4294967296;
    }

    function fitCover(w, h) {
      const s = Math.max(w / img.naturalWidth, h / img.naturalHeight);
      const dw = img.naturalWidth * s, dh = img.naturalHeight * s;
      return { x: (w - dw) * o.focusX, y: (h - dh) * o.focusY, w: dw, h: dh };
    }

    function layout() {
      const r = container.getBoundingClientRect();
      W = Math.max(1, r.width); H = Math.max(1, r.height);
      dpr = Math.min(global.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      cols = Math.ceil(W / o.cell);
      rows = Math.ceil(H / o.cell);
      sample.width = cols; sample.height = rows;

      // 写真の配置(px)をセル単位に換算してサンプリング位置を揃える
      coverPx = fitCover(W, H);
      cover = {
        x: coverPx.x / o.cell, y: coverPx.y / o.cell,
        w: coverPx.w / o.cell, h: coverPx.h / o.cell,
      };

      const n = cols * rows;
      seeds = new Float32Array(n);
      accentMap = new Int8Array(n).fill(-1);
      patchMask = new Uint8Array(n);
      patches = [];
      for (let i = 0; i < n; i++) seeds[i] = rand(i + 1);

      for (const l of [...layers, mix]) { l.width = canvas.width; l.height = canvas.height; }
      for (const g of lctxs) {
        g.setTransform(dpr, 0, 0, dpr, 0, 0);
        g.font = `500 ${Math.round(o.cell * 0.78)}px ${o.font}`;
        g.textAlign = 'center';
        g.textBaseline = 'middle';
      }
      dirty = true;

      calibrate();
      buildSheen();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingQuality = 'high';
    }

    // 輝度を 3%〜97% で正規化し、局所コントラストと彩度のマップを作る
    function calibrate() {
      sctx.clearRect(0, 0, cols, rows);
      sctx.drawImage(img, cover.x, cover.y, cover.w, cover.h);
      const d = sctx.getImageData(0, 0, cols, rows).data;
      const hist = new Uint32Array(256);
      for (let i = 0; i < d.length; i += 4) hist[lum(d[i], d[i + 1], d[i + 2]) | 0]++;
      const total = d.length / 4;
      let acc = 0, a = 0, b = 255;
      for (let i = 0; i < 256; i++) { acc += hist[i]; if (acc < total * 0.03) a = i; if (acc < total * 0.97) b = i; }
      lo = a / 255; hi = Math.max(lo + 0.05, b / 255);

      // 局所コントラスト：縮小→拡大でぼかした画像との差分
      // 白っぽい服や顔のように「暗くはないが背景から浮いている」部分を拾う
      const n = cols * rows;
      contrast = new Float32Array(n);
      chroma = new Float32Array(n);
      const bw = Math.max(2, Math.round(cols / 10)), bh = Math.max(2, Math.round(rows / 10));
      const tiny = document.createElement('canvas');
      tiny.width = bw; tiny.height = bh;
      const tctx = tiny.getContext('2d');
      tctx.imageSmoothingQuality = 'high';
      tctx.drawImage(sample, 0, 0, bw, bh);
      sctx.imageSmoothingQuality = 'high';
      sctx.clearRect(0, 0, cols, rows);
      sctx.drawImage(tiny, 0, 0, cols, rows);
      const blur = sctx.getImageData(0, 0, cols, rows).data;
      const sorted = new Float32Array(n);
      for (let i = 0; i < n; i++) {
        const p = i * 4, r = d[p], g = d[p + 1], b = d[p + 2];
        contrast[i] = sorted[i] = Math.abs(lum(r, g, b) - lum(blur[p], blur[p + 1], blur[p + 2])) / 255;
        const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
        chroma[i] = mx ? Math.max(0, (mx - mn) / mx - 0.08) / 0.5 : 0;
      }
      sorted.sort();
      const cHi = Math.max(0.02, sorted[Math.floor(n * 0.97)]);
      for (let i = 0; i < n; i++) {
        contrast[i] = Math.min(1, Math.max(0, contrast[i] / cHi - 0.25) / 0.75);
        chroma[i] = Math.min(1, chroma[i]);
      }

      palette = extractPalette(d);
    }

    // 色相を12分割し、彩度で重み付けした上位の色を差し色にする
    function extractPalette(d) {
      const bins = Array.from({ length: 12 }, () => [0, 0, 0, 0]);
      for (let p = 0; p < d.length; p += 4) {
        const r = d[p], g = d[p + 1], b = d[p + 2];
        const mx = Math.max(r, g, b), mn = Math.min(r, g, b), L = lum(r, g, b);
        const sat = mx ? (mx - mn) / mx : 0;
        if (sat < 0.18 || L < 25 || L > 230) continue;
        let h;
        if (mx === r) h = ((g - b) / (mx - mn) + 6) % 6;
        else if (mx === g) h = (b - r) / (mx - mn) + 2;
        else h = (r - g) / (mx - mn) + 4;
        const bin = bins[Math.floor(h * 2) % 12], w = sat * sat * (L / 255) * (L / 255); // 鮮やかで明るい色を優先
        bin[0] += r * w; bin[1] += g * w; bin[2] += b * w; bin[3] += w;
      }
      const out = bins.filter((b) => b[3] > 0).sort((a, b) => b[3] - a[3]).slice(0, 5)
        .map((b) => [b[0] / b[3], b[1] / b[3], b[2] / b[3]]);
      return out.length ? out : [[150, 120, 100]];
    }

    function buildSheen() {
      const c = o.cell, pc = document.createElement('canvas');
      pc.width = pc.height = Math.round(c * dpr);
      const g = pc.getContext('2d');
      const grad = g.createLinearGradient(0, 0, pc.width * 0.35, pc.height);
      grad.addColorStop(0, `rgba(255,255,255,${o.sheen})`);
      grad.addColorStop(0.5, 'rgba(255,255,255,0)');
      grad.addColorStop(1, `rgba(0,0,0,${o.sheen * 0.8})`);
      g.fillStyle = grad;
      g.fillRect(0, 0, pc.width, pc.height);
      sheenPattern = lctxs[0].createPattern(pc, 'repeat');
      if (sheenPattern.setTransform) sheenPattern.setTransform(new DOMMatrix().scale(1 / dpr));
    }

    // (r,g,b) を t (-1〜1) だけ明るく/暗くする。色相は保つ
    function shade(r, g, b, t) {
      if (t >= 0) return [r + (255 - r) * t, g + (255 - g) * t, b + (255 - b) * t];
      const k = 1 + t; return [r * k, g * k, b * k];
    }

    function lum(r, g, b) { return 0.2126 * r + 0.7152 * g + 0.0722 * b; }

    // patch モード：矩形を発生・寿命管理してマスクに書き込む
    function stepPatches() {
      patchMask.fill(0);
      patches = patches.filter((p) => --p.life > 0);
      while (patches.length < o.patchMax && Math.random() < o.patchRate) {
        const w = 2 + ((Math.random() * Math.random() * 14) | 0);
        const h = 1 + ((Math.random() * Math.random() * 8) | 0);
        patches.push({
          x: (Math.random() * (cols - w)) | 0,
          y: (Math.random() * (rows - h)) | 0,
          w, h, life: 4 + ((Math.random() * 10) | 0),
        });
      }
      for (const p of patches) {
        for (let y = p.y; y < p.y + p.h; y++) {
          for (let x = p.x; x < p.x + p.w; x++) {
            // 縁をギザギザに欠けさせる
            const edge = x === p.x || y === p.y || x === p.x + p.w - 1 || y === p.y + p.h - 1;
            if (edge && Math.random() < 0.35) continue;
            patchMask[y * cols + x] = 1;
          }
        }
      }
    }

    // 1コマ分のモザイクを、空いている方のレイヤーに描く
    function render(t) {
      const elapsed = t - start;
      const still = reduceMotion;
      cur = 1 - cur;
      const g = lctxs[cur];
      g.clearRect(0, 0, W, H);

      // サンプリング位置を毎コマわずかに揺らす → 輪郭がざわつく
      const jx = still ? 0 : (Math.random() - 0.5) * o.jitter;
      const jy = still ? 0 : (Math.random() - 0.5) * o.jitter;
      sctx.clearRect(0, 0, cols, rows);
      sctx.drawImage(img, cover.x + jx, cover.y + jy, cover.w, cover.h);
      const d = sctx.getImageData(0, 0, cols, rows).data;

      if (o.mode === 'patch' && !still) stepPatches();

      const c = o.cell;
      const introP = o.intro && !still ? Math.min(1, elapsed / o.intro) : 1;
      const hr2 = o.hoverRadius * o.hoverRadius;
      const full = o.mode === 'full';
      const tonal = o.style === 'tonal';
      const cells = []; // [i, r, g, b, ink, flick, alpha] の繰り返し

      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const i = y * cols + x, p = i * 4;
          const cx = x * c + c / 2, cy = y * c + c / 2;

          const r = d[p], gg = d[p + 1], b = d[p + 2];
          let ink = 1 - (lum(r, gg, b) / 255 - lo) / (hi - lo);
          ink = ink < 0 ? 0 : ink > 1 ? 1 : ink;

          let on = full, alpha = 1;
          let flick = o.flicker;

          if (!on && o.mode === 'subject') {
            // 暗さ・コントラスト・彩度をスクリーン合成で統合して被写体を判定
            const extra = Math.min(1, contrast[i] * o.edge + chroma[i] * o.chroma);
            let score = 1 - (1 - ink) * (1 - extra * 0.85);
            if (!still) score += (Math.random() - 0.5) * 0.04; // 境界のセルがゆっくり出入りする
            on = score >= o.threshold && (still || Math.random() < o.density);
            // 輪郭付近は薄く → 写真との境目がぼける
            if (on && score < o.threshold + 0.1) alpha = o.softEdge;
          } else if (!on && o.mode === 'patch') {
            on = patchMask[i] === 1;
          }

          // 導入：最初は全面モザイク、左から順にばらけながら写真へ戻る
          if (introP < 1) {
            const order = (x / cols) * 0.6 + seeds[i] * 0.4;
            if (order > introP) { on = true; alpha = 1; }
          }

          if (mouse.active) {
            const dx = cx - mouse.x, dy = cy - mouse.y, dd = dx * dx + dy * dy;
            if (dd < hr2) {
              const k = 1 - dd / hr2;
              if (Math.random() < k * 1.2) { on = true; alpha = Math.max(alpha, 0.4 + k * 0.6); }
              flick = Math.min(1, flick + k * 0.6);
            }
          }

          if (on) cells.push(i, r, gg, b, ink, flick, alpha);
        }
      }

      // ── タイル
      const groups = new Map(); // 不透明度ごとにまとめて描く
      for (let n = 0; n < cells.length; n += 7) {
        const al = cells[n + 6];
        if (!groups.has(al)) groups.set(al, []);
        groups.get(al).push(n);
      }

      if (tonal) {
        // 低解像度サンプルを補間拡大 → 隣のセルへなめらかに移るグラデーション
        for (const [al, list] of groups) {
          const tiles = new Path2D(), grid = new Path2D();
          for (const n of list) {
            const i = cells[n], px = (i % cols) * c, py = ((i / cols) | 0) * c;
            tiles.rect(px, py, c, c);
            grid.rect(px, py, c, 1);
            grid.rect(px, py + 1, 1, c - 1);
          }
          g.save();
          g.globalAlpha = al;
          g.clip(tiles);
          g.imageSmoothingEnabled = true;
          g.drawImage(sample, 0, 0, cols, rows, 0, 0, cols * c, rows * c);
          if (o.sheen > 0) { g.fillStyle = sheenPattern; g.fill(tiles); }
          g.fillStyle = `rgba(255,255,255,${o.tonalGrid})`;
          g.fill(grid);
          g.restore();
        }
      } else {
        // 白い目地＋単色
        for (let n = 0; n < cells.length; n += 7) {
          const i = cells[n], px = (i % cols) * c, py = ((i / cols) | 0) * c;
          g.globalAlpha = cells[n + 6];
          g.fillStyle = o.gridLine;
          g.fillRect(px, py, c, c);
          g.fillStyle = `rgb(${cells[n + 1]},${cells[n + 2]},${cells[n + 3]})`;
          g.fillRect(px + 1, py + 1, c - 1, c - 1);
        }
      }

      // ── 文字
      const nAccents = tonal ? palette.length : o.accents.length;
      for (let n = 0; n < cells.length; n += 7) {
        const i = cells[n], r = cells[n + 1], gg = cells[n + 2], b = cells[n + 3];
        const ink = cells[n + 4], flick = cells[n + 5];
        const px = (i % cols) * c, py = ((i / cols) | 0) * c;

        let gi = Math.round(ink * (RAMP.length - 1));
        if (!still && Math.random() < flick) gi += Math.random() < 0.5 ? -1 : 1;
        gi = gi < 0 ? 0 : gi >= RAMP.length ? RAMP.length - 1 : gi;

        // 差し色の文字：しばらく持続してから消える
        let a = accentMap[i];
        if (a >= nAccents) a = accentMap[i] = -1;
        if (a < 0 && !still && Math.random() < o.accent / 6) a = accentMap[i] = (Math.random() * nAccents) | 0;
        else if (a >= 0 && Math.random() < 0.06) a = accentMap[i] = -1;

        const L = lum(r, gg, b);
        g.globalAlpha = cells[n + 6] * o.glyphAlpha;
        if (tonal) {
          // 同系色：暗いタイルには少し明るいトーン、明るいタイルには少し暗いトーン
          let col;
          if (a >= 0) {
            const pc = palette[a];
            col = shade(pc[0], pc[1], pc[2], L < 120 ? 0.3 : -0.2);
          } else {
            col = shade(r, gg, b, L < 120 ? 0.34 + ink * 0.14 : -0.36 - ink * 0.2);
          }
          g.fillStyle = `rgb(${col[0] | 0},${col[1] | 0},${col[2] | 0})`;
        } else if (a >= 0) g.fillStyle = o.accents[a];
        else if (L < 120) g.fillStyle = `rgba(255,255,255,${0.6 + ink * 0.3})`;
        else g.fillStyle = `rgba(20,20,20,${0.45 + ink * 0.45})`;
        g.fillText(RAMP[gi], px + c / 2, py + c / 2 + 0.5);
      }
      g.globalAlpha = 1;

      fadeStart = t;
      dirty = true;
    }

    // 写真＋（前コマ→現コマのクロスフェード）を合成して表示
    function compose(t) {
      const f = fadeDur > 0 ? Math.min(1, (t - fadeStart) / fadeDur) : 1;
      ctx.globalAlpha = 1;
      ctx.drawImage(img, coverPx.x, coverPx.y, coverPx.w, coverPx.h);

      // 'lighter' で 前×(1-f) ＋ 現×f を足す → 両方にあるセルは色だけが滑らかに移り、
      // 片方にしかないセルはフェードイン／アウトする
      mctx.setTransform(1, 0, 0, 1, 0, 0);
      mctx.globalCompositeOperation = 'source-over';
      mctx.clearRect(0, 0, mix.width, mix.height);
      mctx.globalCompositeOperation = 'lighter';
      if (f < 1) { mctx.globalAlpha = 1 - f; mctx.drawImage(layers[1 - cur], 0, 0); }
      mctx.globalAlpha = f;
      mctx.drawImage(layers[cur], 0, 0);

      ctx.globalAlpha = o.opacity;
      ctx.drawImage(mix, 0, 0, W, H);
      ctx.globalAlpha = 1;
      dirty = f < 1;
    }

    function loop(t) {
      if (destroyed) return;
      raf = requestAnimationFrame(loop);
      if (!visible || !ready) return;
      const introRunning = o.intro && t - start < o.intro;
      const interval = 1000 / (introRunning ? Math.max(o.fps, 16) : o.fps);
      if (t - last >= interval) {
        last = t;
        fadeDur = interval * o.fade;
        render(t);
      }
      if (dirty) compose(t);
    }

    function redrawStill() { // 静止状態で1枚描く（リサイズ・動きを減らす設定用）
      fadeDur = 0;
      render(start + o.intro + 1);
      compose(performance.now());
    }

    function onResize() { if (ready) { layout(); redrawStill(); } }
    const ro = new ResizeObserver(onResize);
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });

    function onMove(e) {
      const r = container.getBoundingClientRect();
      mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top; mouse.active = true;
    }
    function onLeave() { mouse.active = false; }
    function onVis() { visible = !document.hidden; }

    const fontReady = document.fonts && document.fonts.load
      ? document.fonts.load(`500 ${o.cell}px ${o.font}`, RAMP.join('')).catch(() => {})
      : Promise.resolve();

    img.onload = () => fontReady.then(() => {
      if (destroyed) return;
      try {
        layout();
      } catch (err) {
        // file:// で開いた・別ドメインの画像で CORS 未設定、などで画像の色が読めない
        // → アニメーションは諦め、コンテナの背景画像（静止画）をそのまま見せる
        console.warn('[GlyphHero] 画像のピクセルを読み取れないため、静止画で表示します。' +
          'ローカルサーバー経由で開くか、別ドメインの画像なら crossOrigin と CORS 設定を確認してください。', err);
        canvas.remove();
        return;
      }
      ready = true;
      start = performance.now();
      container.classList.add('glyph-ready');
      if (reduceMotion) { redrawStill(); return; }
      raf = requestAnimationFrame(loop);
    });
    img.src = o.src;

    ro.observe(container);
    io.observe(container);
    if (o.hover) {
      container.addEventListener('pointermove', onMove);
      container.addEventListener('pointerleave', onLeave);
    }
    document.addEventListener('visibilitychange', onVis);

    return {
      set(next) {
        const needLayout = 'cell' in next || 'focusX' in next || 'focusY' in next;
        Object.assign(o, next);
        if ('mode' in next) patches = [];
        if (ready && needLayout) layout();
        else if (ready && 'sheen' in next) buildSheen();
        if (ready && reduceMotion) redrawStill();
      },
      replay() { start = performance.now(); },
      get options() { return Object.assign({}, o); },
      get palette() { return palette.map((c) => c.map((v) => v | 0)); },
      destroy() {
        destroyed = true;
        cancelAnimationFrame(raf);
        ro.disconnect(); io.disconnect();
        container.removeEventListener('pointermove', onMove);
        container.removeEventListener('pointerleave', onLeave);
        document.removeEventListener('visibilitychange', onVis);
        canvas.remove();
      },
    };
  }

  const api = { mount, DEFAULTS };
  if (typeof module === 'object' && module.exports) module.exports = api; // import / require 用
  if (global) global.GlyphHero = api;                                    // <script> 用
})(typeof window !== 'undefined' ? window : null);
